import { Link } from 'react-router-dom';
import { ECOSYSTEM } from '@/constants/content';
import { useReveal } from '@/lib/useReveal';

/**
 * The Founder Ecosystem, animated concentric diagram.
 * Center hub (Toia Wallace) surrounded by three rings of nodes with SVG connectors.
 * All nodes are keyboard-accessible <Link/> elements. Pure CSS animation.
 */
export default function Ecosystem() {
  const ref = useReveal();
  return (
    <section
      ref={ref}
      className="reveal py-32 lg:py-56 bg-white relative overflow-hidden"
      aria-labelledby="ecosystem-title"
      data-testid="home-ecosystem"
    >
      <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20">
        <div className="text-center max-w-3xl mx-auto mb-24">
          <div className="eyebrow text-taupe mb-8">02 · The Ecosystem</div>
          <h2 id="ecosystem-title" className="font-serif-display text-5xl md:text-6xl lg:text-7xl tracking-tightest leading-[0.95] text-matte">
            The Founder <em className="italic text-taupe">Ecosystem.</em>
          </h2>
          <p className="mt-10 text-matte/70 leading-relaxed max-w-xl mx-auto">
            Everything I build is connected by one mission: helping people build smarter
            businesses and create greater opportunities.
          </p>
        </div>

        {/* Desktop diagram, visible on lg+ */}
        <div className="hidden lg:block relative mx-auto" style={{ height: 720, maxWidth: 1100 }}>
          <EcosystemSVG />
        </div>

        {/* Mobile stacked ecosystem, hidden on lg+ */}
        <div className="block lg:!hidden space-y-10">
          <NodeStack node={ECOSYSTEM.hub} isHub />
          {ECOSYSTEM.rings.map((r) => (
            <div key={r.title}>
              <div className="eyebrow text-taupe mb-4 text-center">{r.title}</div>
              <div className="grid grid-cols-2 gap-3">
                {r.nodes.map((n) => (
                  <Link
                    key={n.name}
                    to={n.to}
                    className="block bg-ivory border border-taupe/25 rounded-2xl px-4 py-5 text-center font-serif-display text-lg text-matte hover:border-gold hover:text-gold transition-colors"
                    data-testid={`ecosystem-node-${n.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    {n.name}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function NodeStack({ node, isHub }) {
  return (
    <div className="flex justify-center">
      <div
        className={`px-8 py-6 rounded-full border ${
          isHub ? 'bg-matte text-ivory border-gold' : 'bg-ivory text-matte border-taupe/30'
        }`}
      >
        <div className={`eyebrow ${isHub ? 'text-gold' : 'text-taupe'} text-center mb-1`}>{node.role || ''}</div>
        <div className="font-serif-display text-2xl text-center tracking-tight">{node.label || node.name}</div>
      </div>
    </div>
  );
}

function EcosystemSVG() {
  const cx = 550;
  const cy = 360;
  // Derive rings dynamically so the diagram scales to whatever ECOSYSTEM.rings contains.
  // Distribute rings across the canvas evenly so 2, 3, or 4 rings all read nicely.
  const RING_PRESETS = {
    1: [340],
    2: [260, 420],
    3: [210, 320, 430],
    4: [180, 280, 380, 450],
  };
  const preset = RING_PRESETS[ECOSYSTEM.rings.length] || RING_PRESETS[3];
  const rings = ECOSYSTEM.rings.map((r, i) => ({
    r: preset[i] ?? preset[preset.length - 1],
    nodes: r.nodes,
    label: r.title,
  }));

  return (
    <div className="absolute inset-0">
      {/* SVG hairlines */}
      <svg viewBox="0 0 1100 720" className="absolute inset-0 w-full h-full" aria-hidden>
        <defs>
          <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="rgba(200,161,90,0.22)" />
            <stop offset="100%" stopColor="rgba(200,161,90,0)" />
          </radialGradient>
        </defs>
        <circle cx={cx} cy={cy} r={230} fill="url(#hubGlow)" />
        {rings.map((ring) =>
          ring.nodes.map((_, idx) => {
            const angle = (Math.PI * 2 * idx) / ring.nodes.length - Math.PI / 2;
            const x = cx + Math.cos(angle) * ring.r;
            const y = cy + Math.sin(angle) * ring.r;
            return (
              <line
                key={`${ring.label}-${idx}`}
                x1={cx}
                y1={cy}
                x2={x}
                y2={y}
                stroke="#C8A15A"
                strokeOpacity="0.28"
                strokeWidth="1"
              />
            );
          }),
        )}
        {rings.map((ring) => (
          <circle key={`ring-${ring.r}`} cx={cx} cy={cy} r={ring.r} fill="none" stroke="#B8A99A" strokeOpacity="0.2" strokeDasharray="2 6" />
        ))}
      </svg>

      {/* Center Hub */}
      <div
        className="absolute"
        style={{
          left: cx,
          top: cy,
          transform: 'translate(-50%, -50%)',
        }}
      >
        <div className="w-40 h-40 rounded-full bg-matte text-ivory grid place-items-center border border-gold shadow-[0_0_60px_-10px_rgba(200,161,90,0.4)]">
          <div className="text-center">
            <div className="eyebrow text-gold text-[9px] mb-1">Founder</div>
            <div className="font-serif-display text-2xl tracking-tighter leading-tight">
              Toia
              <br />
              <em className="italic">Wallace</em>
            </div>
          </div>
        </div>
      </div>

      {/* Nodes */}
      {rings.map((ring, ri) =>
        ring.nodes.map((node, idx) => {
          const angle = (Math.PI * 2 * idx) / ring.nodes.length - Math.PI / 2;
          const x = cx + Math.cos(angle) * ring.r;
          const y = cy + Math.sin(angle) * ring.r;
          return (
            <Link
              key={`${ring.label}-${node.name}`}
              to={node.to}
              className="group absolute"
              style={{
                left: x,
                top: y,
                transform: 'translate(-50%, -50%)',
                animation: `float-slow ${9 + (idx % 3)}s ease-in-out infinite`,
                animationDelay: `${idx * 0.4 + ri * 0.3}s`,
              }}
              data-testid={`ecosystem-node-${node.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              <div className="min-w-[140px] px-5 py-3 rounded-full bg-ivory border border-taupe/30 group-hover:border-gold group-hover:shadow-[0_10px_30px_-15px_rgba(200,161,90,0.5)] transition-all duration-500 text-center">
                <div className="font-serif-display text-[15px] text-matte group-hover:text-gold transition-colors leading-tight tracking-tight">
                  {node.name}
                </div>
              </div>
            </Link>
          );
        }),
      )}

      {/* Ring labels */}
      {rings.map((ring) => (
        <div
          key={`label-${ring.r}`}
          className="absolute eyebrow text-taupe/70"
          style={{
            left: cx,
            top: cy - ring.r - 30,
            transform: 'translate(-50%, 0)',
          }}
        >
          {ring.label}
        </div>
      ))}
    </div>
  );
}
